import { SyncPhaseDto } from 'src/app/services/dtos'
import { timeAgoString } from 'src/app/shared/utils'
import { AppStatusState, ProcessStatus } from '../process-state/process-state.component'
import { RelevantStatus } from 'src/app/shared/types'
import { RaisedIssue } from '../../services/issue'

// ─── Shared helpers ──────────────────────────────────────────────

const plural = (n: number, word: string) => `${n} ${word}${n !== 1 ? 's' : ''}`

// Builds a human-readable recovery point timestamp suffix like " from recovery point at 2:30 PM on Apr 6."
function rpContextString(rpTimestamp: Date): string {
  if (!rpTimestamp) return ''
  const time = rpTimestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  const date = rpTimestamp.toLocaleDateString([], { month: 'short', day: 'numeric' })
  return ` from recovery point at ${time} on ${date}.`
}

// Builds a failure context suffix like " at 10:25 AM on Apr 16".
// Shows when the failure occurred so the user has temporal context.
function failureContextString(failureTimestamp: Date): string {
  if (!failureTimestamp) return ''
  const time = failureTimestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  const date = failureTimestamp.toLocaleDateString([], { month: 'short', day: 'numeric' })
  return ` at ${time} on ${date}`
}

// ─── Centralized status text ─────────────────────────────────────
// All user-facing status strings live here. Functions below reference
// these instead of hard-coding strings, so there is one place to
// manage every text variation.

export const STATUS_TEXT = {
  // -- Recovery Environment tab --
  recovery: {
    // Deleted app states
    cleanupFailed: 'Recovery environment cleanup failed.',
    finalizingDeletion: 'Finalizing application deletion...',
    cleaningUp: 'Cleaning up recovery environment resources...',

    // Standby — "restore ... to standby" distinguishes from failover restore
    firstBackupInProgress: 'Initial backup in progress. Restore to standby will begin automatically once complete.',
    waitingForFirstBackup: 'Waiting for initial backup to complete before restoring to standby.',
    waitingForInitialRestore: 'Waiting to perform initial restore to standby...',
    performingInitialRestore: 'Performing initial restore to standby...',
    restoringBackup: (ta: string) => `Restoring backup from ${ta} to standby...`,
    successfullyRestored: (ta: string) => `Successfully restored backup from ${ta} to standby.`,
    failedToRestoreCount: (n: number, ta: string) => `Failed to restore ${plural(n, 'resource')} from backup ${ta}.`,
    initialRestoreFailed: 'Initial restore to standby failed.',
    failedToApplyBackup: (ta: string) => `Failed to restore backup from ${ta} to standby.`,
    standbyRestoreBlocked: (sourceRegion: string, targetRegion: string) =>
      `Another application is in recovery from ${sourceRegion} to ${targetRegion}. Standby restore will continue automatically once that recovery concludes.`,

    // Failover / test failover — "restore for [test] recovery" distinguishes from standby restore
    preparingRestore: (isTest: boolean) => `Preparing to restore for ${isTest ? 'test recovery' : 'recovery'}...`,
    restoreInProgress: (isTest: boolean) => (isTest ? 'Restoring for test recovery...' : 'Restoring for recovery...'),
    allResourcesRestored: (ta: string, isTest: boolean) =>
      `All resources from backup ${ta} have been successfully restored for ${isTest ? 'test recovery' : 'recovery'}.`,
    restoreFailed: (isTest: boolean) => (isTest ? 'Restore for test recovery failed.' : 'Restore for recovery failed.'),

    // Failed link parts
    failedLinkRestore: (ta: string) => ({ prefix: 'Failed to restore' as const, suffix: `from backup ${ta}.` }),

    // Error fallback
    unableToCompute: (phase: string, status: string) =>
      `Unable to compute recovery environment status for ${phase}, ${status}.`,
  },

  // -- Backup Health tab --
  backup: {
    creatingInitial: 'Creating initial backup...',
    creatingNew: 'Creating backup...',
    planningBackup: 'Planning backup...',
    lastSuccessful: (ta: string, rpCtx: string) => `Last successful backup: ${ta}${rpCtx || '.'}`,
    rpoViolation: (rpCtx: string) => `Backup is out of compliance with RPO${rpCtx || '.'}`,
    failedToBackupCount: (n: number, failureCtx: string) => `Failed to back up ${plural(n, 'resource')}${failureCtx}.`,
    failedToCapture: (failureCtx: string) => `Failed to create backup${failureCtx}.`,
    noDataAvailable: 'No backup data available.',
    // Prior backup info shown at far right during active backup.
    // "Last successful backup" + "(N resources)" avoids the N/N format that visually
    // mimics the active progress counter and confused users.
    priorBackupInfo: (ta: string, rpCtx: string, total: number) => {
      // rpCtx ends with "." — strip it so we can append "(N resources)." cleanly
      const rpCtxTrimmed = rpCtx.endsWith('.') ? rpCtx.slice(0, -1) : rpCtx
      return `Last successful backup: ${ta}${rpCtxTrimmed} (${plural(total, 'resource')}).`
    },
    // Failed link parts
    failedLinkBackup: { prefix: 'Failed to back up' as const },
  },

  // -- Failback sub-tab --
  failback: {
    available: (n: number) => `${plural(n, 'resource')} available for failback to primary environment.`,
    noSupported: 'No resources in this recovery point support failback.',
    returning: 'Returning resources to primary environment...',
    retrying: 'Retrying return to primary environment...',
    allReturned: 'All resources have been successfully returned to primary environment.',
    failed: 'Failback to primary environment failed.',
    failedToReturnCount: (n: number) => `Failed to return ${plural(n, 'resource')} to primary environment.`,
    // Failed link parts
    failedLinkReturn: { prefix: 'Failed to return' as const, suffix: 'to primary environment.' },
  },
}

// ─── Interfaces ──────────────────────────────────────────────────

// Parts of a clickable "N failed" link — template assembles as "prefix N resources suffix"
export interface TabStatusFailedLink {
  prefix: string // e.g. "Failed to restore"
  count: number // e.g. 3
  suffix: string // e.g. "from backup 2 hours ago"
}

export interface RecoveryEnvironmentStatus {
  state: AppStatusState
  text: string
  ratioText?: string
  progressCount?: number
  progressTotal?: number
  failedLink?: TabStatusFailedLink
  issues: RaisedIssue[]
}

export interface BackupHealthStatus {
  state: AppStatusState
  text: string
  ratioText?: string
  progressCount?: number
  progressTotal?: number
  failedLink?: TabStatusFailedLink
  issues: RaisedIssue[]
  // Prior backup summary shown at far right during active backup
  priorBackupInfo?: string
}

export interface FailbackStatus {
  state: AppStatusState
  text: string
  ratioText?: string
  progressCount?: number
  progressTotal?: number
  failedLink?: TabStatusFailedLink
  issues: RaisedIssue[]
}

// ─── Recovery Environment Status ─────────────────────────────────

// Passed from the backup tab so recovery can show appropriate messaging before first backup completes
export interface BackupContext {
  isBackingUp: boolean
  hasEverSucceeded: boolean
}

// Bundles all inputs for recovery environment status computation.
// Reduces the 12-arg function signature to a single context object.
export interface RecoveryStatusContext {
  syncPhase: SyncPhaseDto
  syncStatus: ProcessStatus
  appliedRpTimestamp: Date
  failedRestoreCount: number
  restoreIssues: RaisedIssue[]
  appIsDeleted: boolean
  allResourcesCleaned: boolean
  successCount: number
  totalCount: number
  lastRestoreSuccess?: Date
  backupContext?: BackupContext
  inProgressCount: number
  // Set by the caller when a sibling-app failover is deferring this app's standby restore.
  standbyRestoreBlockedBySibling?: { sourceRegion: string; targetRegion: string }
}

// True while a restore/failback is in progress but nothing has resolved yet — e.g. just after a
// resume, before the backend republishes the already-restored resources, so the resource-derived
// successCount is transiently 0 (stale prior FAILED records are masked to PENDING during
// processing, so failedCount is 0 too). Callers suppress the progress bar/ratio in this window so
// the indeterminate "Restoring…" spinner stands instead of a misleading reset to 0%. The window
// ends as soon as any resource resolves — a fresh success OR a fresh (post-horizon) failure — at
// which point real progress and any failure count render again.
function isRestoreWarmingUp(successCount: number, inProgressCount: number, failedCount: number): boolean {
  return successCount === 0 && failedCount === 0 && inProgressCount > 0
}

export function computeRecoveryEnvStatus(ctx: RecoveryStatusContext): RecoveryEnvironmentStatus {
  const {
    syncPhase,
    syncStatus,
    appliedRpTimestamp,
    failedRestoreCount,
    restoreIssues,
    appIsDeleted,
    allResourcesCleaned,
    successCount,
    totalCount,
    lastRestoreSuccess,
    backupContext,
    inProgressCount,
    standbyRestoreBlockedBySibling,
  } = ctx
  const taString = appliedRpTimestamp ? timeAgoString(appliedRpTimestamp) : '\u2026'
  const pct = totalCount > 0 ? Math.floor((successCount / totalCount) * 100) : 0
  const rpTimeLabel = appliedRpTimestamp
    ? appliedRpTimestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : null
  const rpDateLabel = appliedRpTimestamp
    ? appliedRpTimestamp.toLocaleDateString([], { month: 'short', day: 'numeric' })
    : null
  const rpContext = rpTimeLabel ? ` from recovery point at ${rpTimeLabel} on ${rpDateLabel}` : ''
  const ratioSuffix = totalCount > 0 ? `${pct}% restored (${successCount}/${totalCount})${rpContext}.` : ''

  // Progress bar for recovery/failback — shows resource-level completion during active restores
  const progressData =
    totalCount > 0 && !isRestoreWarmingUp(successCount, inProgressCount, failedRestoreCount)
      ? { progressCount: successCount, progressTotal: totalCount }
      : {}

  // Deleted app states take priority over all phase-based logic
  if (appIsDeleted) {
    if (restoreIssues.length > 0) {
      return { state: AppStatusState.ERROR, text: STATUS_TEXT.recovery.cleanupFailed, issues: restoreIssues }
    }
    if (allResourcesCleaned) {
      return { state: AppStatusState.INDETERMINATE, text: STATUS_TEXT.recovery.finalizingDeletion, issues: [] }
    }
    return { state: AppStatusState.INDETERMINATE, text: STATUS_TEXT.recovery.cleaningUp, issues: [] }
  }

  switch (syncPhase) {
    case SyncPhaseDto.STANDBY:
      return computeRecoveryEnvStandby(
        syncStatus,
        taString,
        failedRestoreCount,
        restoreIssues,
        ratioSuffix,
        successCount,
        totalCount,
        lastRestoreSuccess,
        backupContext,
        inProgressCount,
        progressData,
        standbyRestoreBlockedBySibling
      )
    // Failover and test failover share logic, differing only in user-facing label
    case SyncPhaseDto.FAILOVER:
      return computeRecoveryEnvFailover(
        syncStatus,
        taString,
        failedRestoreCount,
        restoreIssues,
        false,
        ratioSuffix,
        successCount,
        totalCount,
        inProgressCount,
        progressData
      )
    case SyncPhaseDto.FAILOVER_TEST:
      return computeRecoveryEnvFailover(
        syncStatus,
        taString,
        failedRestoreCount,
        restoreIssues,
        true,
        ratioSuffix,
        successCount,
        totalCount,
        inProgressCount,
        progressData
      )
  }

  return {
    state: AppStatusState.ERROR,
    text: STATUS_TEXT.recovery.unableToCompute(syncPhase, syncStatus),
    issues: restoreIssues,
  }
}

function computeRecoveryEnvStandby(
  syncStatus: ProcessStatus,
  taString: string,
  failedCount: number,
  issues: RaisedIssue[],
  ratioSuffix: string,
  successCount = 0,
  totalCount = 0,
  lastRestoreSuccess?: Date,
  backupContext?: BackupContext,
  inProgressCount = 0,
  progressData: Partial<RecoveryEnvironmentStatus> = {},
  blockedBySibling?: { sourceRegion: string; targetRegion: string }
): RecoveryEnvironmentStatus {
  // Distinguish first-ever restore from subsequent ones for different failure messaging
  const isFirstRestore = !lastRestoreSuccess

  // Override only when a restore would otherwise start: no in-flight work, no terminal failures.
  const blockedReady =
    !!blockedBySibling &&
    inProgressCount === 0 &&
    failedCount === 0 &&
    (syncStatus === ProcessStatus.NEVER_ATTEMPTED || syncStatus === ProcessStatus.MOST_RECENT_SUCCEEDED)

  switch (syncStatus) {
    case ProcessStatus.NEVER_ATTEMPTED: {
      // Before first backup completes, show backup-aware messaging instead of generic "waiting"
      if (backupContext && !backupContext.hasEverSucceeded) {
        const text = backupContext.isBackingUp
          ? STATUS_TEXT.recovery.firstBackupInProgress
          : STATUS_TEXT.recovery.waitingForFirstBackup
        return { state: AppStatusState.INDETERMINATE, text, issues: [] }
      }
      if (blockedReady) {
        return {
          // Idle/waiting — nothing is running, so use INFO (no spinner) rather than INDETERMINATE.
          state: AppStatusState.INFO,
          text: STATUS_TEXT.recovery.standbyRestoreBlocked(
            blockedBySibling.sourceRegion,
            blockedBySibling.targetRegion
          ),
          issues: [],
        }
      }
      return { state: AppStatusState.INDETERMINATE, text: STATUS_TEXT.recovery.waitingForInitialRestore, issues: [] }
    }
    case ProcessStatus.FIRST_ATTEMPT_IN_PROGRESS:
    case ProcessStatus.SUBSEQUENT_ATTEMPT_IN_PROGRESS: {
      // All resources reached a terminal state despite process-level status still showing
      // in-progress — the backend hasn't updated lastRestoreSuccess yet. Show the actual
      // terminal state based on resource outcomes instead of a stale "Restoring..." spinner.
      if (totalCount > 0 && inProgressCount === 0) {
        if (failedCount > 0) {
          return {
            state: AppStatusState.ERROR,
            text: STATUS_TEXT.recovery.failedToRestoreCount(failedCount, taString),
            ratioText: ratioSuffix,
            failedLink: { ...STATUS_TEXT.recovery.failedLinkRestore(taString), count: failedCount },
            issues,
          }
        }
        if (issues.length > 0) {
          return {
            state: AppStatusState.WARNING,
            text: STATUS_TEXT.recovery.successfullyRestored(taString),
            ratioText: ratioSuffix,
            issues,
          }
        }
        return {
          state: AppStatusState.SUCCESS,
          text: STATUS_TEXT.recovery.successfullyRestored(taString),
          ratioText: ratioSuffix,
          issues: [],
        }
      }
      // Resources still being processed — show in-progress spinner
      const inProgressText =
        syncStatus === ProcessStatus.FIRST_ATTEMPT_IN_PROGRESS
          ? STATUS_TEXT.recovery.performingInitialRestore
          : STATUS_TEXT.recovery.restoringBackup(taString)
      return {
        state: AppStatusState.INDETERMINATE,
        text: inProgressText,
        ratioText: isRestoreWarmingUp(successCount, inProgressCount, failedCount)
          ? undefined
          : ratioSuffix || undefined,
        ...progressData,
        issues: [],
      }
    }
    case ProcessStatus.MOST_RECENT_SUCCEEDED:
      // Resources still being processed — show in-progress rather than terminal state.
      // No staleness timeout: backend restores (especially EKS) can legitimately take
      // hours. As long as resources are PENDING/PROCESSING without an API error, we
      // trust they're still working and keep the spinner. The only exit paths are:
      // resources reaching a terminal status, or the process-level status changing.
      if (inProgressCount > 0) {
        return {
          state: AppStatusState.INDETERMINATE,
          text: STATUS_TEXT.recovery.restoringBackup(taString),
          ratioText: isRestoreWarmingUp(successCount, inProgressCount, failedCount)
            ? undefined
            : ratioSuffix || undefined,
          ...progressData,
          issues: [],
        }
      }
      if (failedCount > 0) {
        // Show failed resource count — no "incomplete" intermediate state in standby
        return {
          state: AppStatusState.ERROR,
          text: STATUS_TEXT.recovery.failedToRestoreCount(failedCount, taString),
          ratioText: ratioSuffix,
          failedLink: { ...STATUS_TEXT.recovery.failedLinkRestore(taString), count: failedCount },
          issues,
        }
      }
      if (blockedReady) {
        return {
          // Idle/waiting — nothing is running, so use INFO (no spinner) rather than INDETERMINATE.
          state: AppStatusState.INFO,
          text: STATUS_TEXT.recovery.standbyRestoreBlocked(
            blockedBySibling.sourceRegion,
            blockedBySibling.targetRegion
          ),
          issues: [],
        }
      }
      if (issues.length > 0) {
        return {
          state: AppStatusState.WARNING,
          text: STATUS_TEXT.recovery.successfullyRestored(taString),
          ratioText: ratioSuffix,
          issues,
        }
      }
      return {
        state: AppStatusState.SUCCESS,
        text: STATUS_TEXT.recovery.successfullyRestored(taString),
        ratioText: ratioSuffix,
        issues: [],
      }
    case ProcessStatus.MOST_RECENT_FAILED:
      if (failedCount > 0) {
        return {
          state: AppStatusState.ERROR,
          text: STATUS_TEXT.recovery.failedToRestoreCount(failedCount, taString),
          ratioText: ratioSuffix,
          failedLink: { ...STATUS_TEXT.recovery.failedLinkRestore(taString), count: failedCount },
          issues,
        }
      }
      // All resources succeeded despite process-level failure timestamp —
      // resource statuses have caught up. Show success/warning, not error.
      if (totalCount > 0 && successCount === totalCount) {
        if (issues.length > 0) {
          return {
            state: AppStatusState.WARNING,
            text: STATUS_TEXT.recovery.successfullyRestored(taString),
            ratioText: ratioSuffix,
            issues,
          }
        }
        return {
          state: AppStatusState.SUCCESS,
          text: STATUS_TEXT.recovery.successfullyRestored(taString),
          ratioText: ratioSuffix,
          issues: [],
        }
      }
      if (isFirstRestore) {
        return {
          state: AppStatusState.ERROR,
          text: STATUS_TEXT.recovery.initialRestoreFailed,
          ratioText: ratioSuffix,
          issues,
        }
      }
      return {
        state: AppStatusState.ERROR,
        text: STATUS_TEXT.recovery.failedToApplyBackup(taString),
        ratioText: ratioSuffix,
        issues,
      }
  }
  // Fallback for unexpected syncStatus values — safe default rather than error
  return { state: AppStatusState.INDETERMINATE, text: STATUS_TEXT.recovery.waitingForInitialRestore, issues: [] }
}

// Handles both FAILOVER and FAILOVER_TEST phases via the isTest flag
function computeRecoveryEnvFailover(
  syncStatus: ProcessStatus,
  taString: string,
  failedCount: number,
  issues: RaisedIssue[],
  isTest: boolean,
  ratioSuffix: string,
  successCount = 0,
  totalCount = 0,
  inProgressCount = 0,
  progressData: Partial<RecoveryEnvironmentStatus> = {}
): RecoveryEnvironmentStatus {
  switch (syncStatus) {
    case ProcessStatus.NEVER_ATTEMPTED:
      return {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.recovery.preparingRestore(isTest),
        issues: [],
      }
    // Both first and subsequent attempts show the same in-progress text for failover
    case ProcessStatus.FIRST_ATTEMPT_IN_PROGRESS:
    case ProcessStatus.SUBSEQUENT_ATTEMPT_IN_PROGRESS: {
      // All resources reached a terminal state despite process-level status still showing
      // in-progress — the backend hasn't updated lastRestoreSuccess yet. Show the actual
      // terminal state based on resource outcomes instead of a stale spinner.
      if (totalCount > 0 && inProgressCount === 0) {
        if (failedCount > 0) {
          return {
            state: AppStatusState.ERROR,
            text: STATUS_TEXT.recovery.failedToRestoreCount(failedCount, taString),
            ratioText: ratioSuffix,
            failedLink: { ...STATUS_TEXT.recovery.failedLinkRestore(taString), count: failedCount },
            issues,
          }
        }
        return {
          state: AppStatusState.SUCCESS,
          text: STATUS_TEXT.recovery.allResourcesRestored(taString, isTest),
          ratioText: ratioSuffix,
          issues,
        }
      }
      return {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.recovery.restoreInProgress(isTest),
        ratioText: isRestoreWarmingUp(successCount, inProgressCount, failedCount)
          ? undefined
          : ratioSuffix || undefined,
        ...progressData,
        issues: [],
      }
    }
    case ProcessStatus.MOST_RECENT_FAILED:
      if (failedCount > 0) {
        return {
          state: AppStatusState.ERROR,
          text: STATUS_TEXT.recovery.failedToRestoreCount(failedCount, taString),
          ratioText: ratioSuffix,
          failedLink: { ...STATUS_TEXT.recovery.failedLinkRestore(taString), count: failedCount },
          issues,
        }
      }
      // All resources succeeded despite process-level failure timestamp
      if (totalCount > 0 && successCount === totalCount) {
        return {
          state: AppStatusState.SUCCESS,
          text: STATUS_TEXT.recovery.allResourcesRestored(taString, isTest),
          ratioText: ratioSuffix,
          issues,
        }
      }
      return {
        state: AppStatusState.ERROR,
        text: STATUS_TEXT.recovery.restoreFailed(isTest),
        ratioText: ratioSuffix,
        issues,
      }
    case ProcessStatus.MOST_RECENT_SUCCEEDED:
      // Resources still being processed — show in-progress rather than terminal state.
      // No staleness timeout: backend restores (especially EKS) can legitimately take
      // hours. As long as resources are PENDING/PROCESSING without an API error, we
      // trust they're still working and keep the spinner.
      if (inProgressCount > 0) {
        return {
          state: AppStatusState.INDETERMINATE,
          text: STATUS_TEXT.recovery.restoreInProgress(isTest),
          ratioText: isRestoreWarmingUp(successCount, inProgressCount, failedCount)
            ? undefined
            : ratioSuffix || undefined,
          ...progressData,
          issues: [],
        }
      }
      if (failedCount > 0) {
        // Show failed resource count — no "incomplete" intermediate state
        return {
          state: AppStatusState.ERROR,
          text: STATUS_TEXT.recovery.failedToRestoreCount(failedCount, taString),
          ratioText: ratioSuffix,
          failedLink: { ...STATUS_TEXT.recovery.failedLinkRestore(taString), count: failedCount },
          issues,
        }
      }
      return {
        state: AppStatusState.SUCCESS,
        text: STATUS_TEXT.recovery.allResourcesRestored(taString, isTest),
        ratioText: ratioSuffix,
        issues,
      }
  }
  return { state: AppStatusState.INDETERMINATE, text: STATUS_TEXT.recovery.preparingRestore(isTest), issues: [] }
}

// ─── Backup Health Status ────────────────────────────────────────

// Bundles all inputs for backup health status computation.
export interface BackupStatusContext {
  syncPhase: SyncPhaseDto
  stagingStatus: ProcessStatus
  rpTimestamp: Date | null
  failedBackupCount: number
  backupIssues: RaisedIssue[]
  scanIssues: RaisedIssue[]
  isRpoViolation: boolean
  isBackupActive: boolean
  successCount: number
  totalCount: number
  lastBackupStart?: Date
  lastBackupSuccess?: Date
  lastBackupFailure?: Date
  hasQueuedResources: boolean
}

export function computeBackupHealthStatus(ctx: BackupStatusContext): BackupHealthStatus {
  const {
    syncPhase,
    stagingStatus,
    rpTimestamp: appliedRpTimestamp,
    failedBackupCount,
    backupIssues,
    scanIssues,
    isRpoViolation,
    isBackupActive,
    successCount,
    totalCount,
    lastBackupStart,
    lastBackupSuccess,
    lastBackupFailure,
    hasQueuedResources,
  } = ctx
  const issues = [...backupIssues, ...scanIssues]

  // Active backup takes highest priority — can run during failover/test phases too
  if (isBackupActive) {
    const isFirst = !lastBackupSuccess
    const label = isFirst ? STATUS_TEXT.backup.creatingInitial : STATUS_TEXT.backup.creatingNew
    const progress: Partial<BackupHealthStatus> =
      totalCount > 0 ? { progressCount: successCount, progressTotal: totalCount } : {}
    // Show prior backup summary at far right when a previous backup exists
    const rpCtx = appliedRpTimestamp ? rpContextString(appliedRpTimestamp) : ''
    const priorBackupInfo =
      !isFirst && appliedRpTimestamp
        ? STATUS_TEXT.backup.priorBackupInfo(timeAgoString(appliedRpTimestamp), rpCtx, totalCount)
        : undefined
    return { state: AppStatusState.INDETERMINATE, text: label, priorBackupInfo, ...progress, issues: [] }
  }

  // Non-standby phases freeze backup display at last known result (no resource-level checks)
  if (syncPhase !== SyncPhaseDto.STANDBY) {
    return computeBackupHealthLastKnown(stagingStatus, appliedRpTimestamp, failedBackupCount, issues, lastBackupFailure)
  }

  // Resources are queued but sync pair timestamps haven't caught up yet —
  // show "Planning backup..." so the user sees an active phase indicator
  if (hasQueuedResources) {
    const priorInfo = appliedRpTimestamp
      ? STATUS_TEXT.backup.lastSuccessful(timeAgoString(appliedRpTimestamp), rpContextString(appliedRpTimestamp))
      : undefined
    return {
      state: AppStatusState.INDETERMINATE,
      text: STATUS_TEXT.backup.planningBackup,
      priorBackupInfo: priorInfo,
      issues: [],
    }
  }

  // Standby phase — full backup health
  return computeBackupHealthStandby(
    stagingStatus,
    appliedRpTimestamp,
    failedBackupCount,
    issues,
    isRpoViolation,
    successCount,
    totalCount,
    lastBackupFailure
  )
}

function computeBackupHealthStandby(
  stagingStatus: ProcessStatus,
  rpTimestamp: Date | null,
  failedCount: number,
  issues: RaisedIssue[],
  isRpoViolation: boolean,
  successCount = 0,
  totalCount = 0,
  lastBackupFailure?: Date
): BackupHealthStatus {
  switch (stagingStatus) {
    case ProcessStatus.NEVER_ATTEMPTED:
      return { state: AppStatusState.INDETERMINATE, text: STATUS_TEXT.backup.planningBackup, issues: [] }
    case ProcessStatus.FIRST_ATTEMPT_IN_PROGRESS: {
      const progress: Partial<BackupHealthStatus> =
        totalCount > 0 ? { progressCount: successCount, progressTotal: totalCount } : {}
      return {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.backup.creatingInitial,
        ...progress,
        issues: [],
      }
    }
    case ProcessStatus.SUBSEQUENT_ATTEMPT_IN_PROGRESS: {
      const progress: Partial<BackupHealthStatus> =
        totalCount > 0 ? { progressCount: successCount, progressTotal: totalCount } : {}
      const rpCtx = rpTimestamp ? rpContextString(rpTimestamp) : ''
      const priorInfo = rpTimestamp
        ? STATUS_TEXT.backup.priorBackupInfo(timeAgoString(rpTimestamp), rpCtx, totalCount)
        : undefined
      return {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.backup.creatingNew,
        priorBackupInfo: priorInfo,
        ...progress,
        issues: [],
      }
    }
    case ProcessStatus.MOST_RECENT_SUCCEEDED: {
      const rpCtx = rpContextString(rpTimestamp)
      // Future phase: RPO violation detection not yet wired up (hardcoded false in caller)
      // if (isRpoViolation) {
      //   return {
      //     state: AppStatusState.WARNING,
      //     text: STATUS_TEXT.backup.rpoViolation(rpCtx),
      //     issues,
      //   }
      // }
      const taString = rpTimestamp ? timeAgoString(rpTimestamp) : '\u2026'
      if (issues.length > 0) {
        return {
          state: AppStatusState.WARNING,
          text: STATUS_TEXT.backup.lastSuccessful(taString, rpCtx),
          issues,
        }
      }
      // Only treat as degraded when some resources have actually been processed in the
      // current backup cycle. When no resources show SUCCEEDED or FAILED (all NO_STATUS),
      // backup resource statuses haven't caught up with the process-level success yet —
      // e.g., after initial restore completes and before the first backup cycle runs.
      if (totalCount > 0 && successCount < totalCount && successCount + failedCount > 0) {
        return {
          state: AppStatusState.WARNING,
          text: STATUS_TEXT.backup.lastSuccessful(taString, rpCtx),
          issues: [],
        }
      }
      return {
        state: AppStatusState.SUCCESS,
        text: STATUS_TEXT.backup.lastSuccessful(taString, rpCtx),
        issues: [],
      }
    }
    case ProcessStatus.MOST_RECENT_FAILED: {
      // "Last successful backup..." renders via priorBackupInfo so it inherits
      // substatus styling instead of the bold error headline color.
      const failureCtx = failureContextString(lastBackupFailure)
      const rpCtx = rpContextString(rpTimestamp)
      const priorInfo = rpTimestamp ? STATUS_TEXT.backup.lastSuccessful(timeAgoString(rpTimestamp), rpCtx) : undefined
      if (failedCount > 0) {
        return {
          state: AppStatusState.ERROR,
          text: STATUS_TEXT.backup.failedToBackupCount(failedCount, failureCtx),
          failedLink: { ...STATUS_TEXT.backup.failedLinkBackup, count: failedCount, suffix: `${failureCtx}.` },
          priorBackupInfo: priorInfo,
          issues,
        }
      }
      return {
        state: AppStatusState.ERROR,
        text: STATUS_TEXT.backup.failedToCapture(failureCtx),
        priorBackupInfo: priorInfo,
        issues,
      }
    }
  }
  return { state: AppStatusState.INDETERMINATE, text: STATUS_TEXT.backup.planningBackup, issues: [] }
}

// Simplified backup status for non-standby phases — skips resource-level counts
function computeBackupHealthLastKnown(
  stagingStatus: ProcessStatus,
  rpTimestamp: Date | null,
  failedCount: number,
  issues: RaisedIssue[],
  lastBackupFailure?: Date
): BackupHealthStatus {
  // Show the most recent result from the last backup cycle.
  // Note: successCount/totalCount are intentionally not checked here —
  // during non-standby phases (recovery test, failover) resources may be
  // queued for the next cycle, which doesn't mean the last backup was degraded.
  switch (stagingStatus) {
    // SUBSEQUENT_ATTEMPT_IN_PROGRESS treated as success — backup started but phase changed
    case ProcessStatus.MOST_RECENT_SUCCEEDED:
    case ProcessStatus.SUBSEQUENT_ATTEMPT_IN_PROGRESS: {
      const rpCtx = rpContextString(rpTimestamp)
      const taString = rpTimestamp ? timeAgoString(rpTimestamp) : '\u2026'
      if (issues.length > 0) {
        return {
          state: AppStatusState.WARNING,
          text: STATUS_TEXT.backup.lastSuccessful(taString, rpCtx),
          issues,
        }
      }
      return {
        state: AppStatusState.SUCCESS,
        text: STATUS_TEXT.backup.lastSuccessful(taString, rpCtx),
        issues: [],
      }
    }
    case ProcessStatus.MOST_RECENT_FAILED: {
      // Prior backup renders via priorBackupInfo substatus slot, not inline in text.
      const failureCtx = failureContextString(lastBackupFailure)
      const rpCtx = rpContextString(rpTimestamp)
      const priorInfo = rpTimestamp ? STATUS_TEXT.backup.lastSuccessful(timeAgoString(rpTimestamp), rpCtx) : undefined
      if (failedCount > 0) {
        return {
          state: AppStatusState.ERROR,
          text: STATUS_TEXT.backup.failedToBackupCount(failedCount, failureCtx),
          failedLink: { ...STATUS_TEXT.backup.failedLinkBackup, count: failedCount, suffix: `${failureCtx}.` },
          priorBackupInfo: priorInfo,
          issues,
        }
      }
      return {
        state: AppStatusState.ERROR,
        text: STATUS_TEXT.backup.failedToCapture(failureCtx),
        priorBackupInfo: priorInfo,
        issues,
      }
    }
    default: {
      const rpCtx = rpContextString(rpTimestamp)
      const taString = rpTimestamp ? timeAgoString(rpTimestamp) : '\u2026'
      // Empty/no-data is a neutral terminal state, not in-progress \u2014 INFO avoids the spinner glyph.
      return {
        state: rpTimestamp ? AppStatusState.INDETERMINATE : AppStatusState.INFO,
        text: rpTimestamp ? STATUS_TEXT.backup.lastSuccessful(taString, rpCtx) : STATUS_TEXT.backup.noDataAvailable,
        issues: [],
      }
    }
  }
}

// ─── Failback Status ─────────────────────────────────────────────

/**
 * Computes the status bar state for the failback sub-tab.
 * Shows resource availability count when failback has never been attempted.
 */
// Bundles all inputs for failback status computation.
export interface FailbackStatusContext {
  processStatus: ProcessStatus
  failedCount: number
  issues: RaisedIssue[]
  successCount: number
  totalCount: number
  inProgressCount: number
}

export function computeFailbackStatus(ctx: FailbackStatusContext): FailbackStatus | null {
  const {
    processStatus: failbackProcessStatus,
    failedCount: failedFailbackCount,
    issues: failbackIssues,
    successCount,
    totalCount,
    inProgressCount,
  } = ctx
  const pct = totalCount > 0 ? Math.floor((successCount / totalCount) * 100) : 0
  const ratioSuffix = totalCount > 0 ? `${pct}% returned (${successCount}/${totalCount}).` : ''
  const progressData =
    totalCount > 0 && !isRestoreWarmingUp(successCount, inProgressCount, failedFailbackCount)
      ? { progressCount: successCount, progressTotal: totalCount }
      : {}

  switch (failbackProcessStatus) {
    case ProcessStatus.NEVER_ATTEMPTED:
      // No resources in this recovery point support failback — neutral info, not SUCCESS,
      // since there's nothing to celebrate and nothing to do.
      if (totalCount === 0) {
        return {
          state: AppStatusState.INFO,
          text: STATUS_TEXT.failback.noSupported,
          issues: [],
        }
      }
      // Show how many resources are available for failback — no spinner, nothing is in progress
      return {
        state: AppStatusState.SUCCESS,
        text: STATUS_TEXT.failback.available(totalCount),
        issues: [],
      }

    case ProcessStatus.FIRST_ATTEMPT_IN_PROGRESS:
    case ProcessStatus.SUBSEQUENT_ATTEMPT_IN_PROGRESS: {
      // All resources reached a terminal state despite process-level status still showing
      // in-progress — the backend hasn't updated lastSuccess yet. Show the actual
      // terminal state based on resource outcomes instead of a stale spinner.
      if (totalCount > 0 && inProgressCount === 0) {
        if (failedFailbackCount > 0) {
          return {
            state: AppStatusState.ERROR,
            text: STATUS_TEXT.failback.failedToReturnCount(failedFailbackCount),
            ratioText: ratioSuffix,
            failedLink: { ...STATUS_TEXT.failback.failedLinkReturn, count: failedFailbackCount },
            issues: failbackIssues,
          }
        }
        return {
          state: AppStatusState.SUCCESS,
          text: STATUS_TEXT.failback.allReturned,
          ratioText: ratioSuffix,
          issues: failbackIssues.length > 0 ? failbackIssues : [],
        }
      }
      // Resources still being processed — show in-progress spinner with progress bar
      const inProgressText =
        failbackProcessStatus === ProcessStatus.FIRST_ATTEMPT_IN_PROGRESS
          ? STATUS_TEXT.failback.returning
          : STATUS_TEXT.failback.retrying
      return {
        state: AppStatusState.INDETERMINATE,
        text: inProgressText,
        ratioText: isRestoreWarmingUp(successCount, inProgressCount, failedFailbackCount)
          ? undefined
          : ratioSuffix || undefined,
        ...progressData,
        issues: [],
      }
    }

    case ProcessStatus.MOST_RECENT_SUCCEEDED:
      // Resource-level failures override process-level success (partial failback)
      if (failedFailbackCount > 0) {
        return {
          state: AppStatusState.ERROR,
          text: STATUS_TEXT.failback.failedToReturnCount(failedFailbackCount),
          ratioText: ratioSuffix,
          failedLink: { ...STATUS_TEXT.failback.failedLinkReturn, count: failedFailbackCount },
          issues: failbackIssues,
        }
      }
      return {
        state: AppStatusState.SUCCESS,
        text: STATUS_TEXT.failback.allReturned,
        ratioText: ratioSuffix,
        issues: failbackIssues.length > 0 ? failbackIssues : [],
      }

    case ProcessStatus.MOST_RECENT_FAILED:
      if (failedFailbackCount > 0) {
        return {
          state: AppStatusState.ERROR,
          text: STATUS_TEXT.failback.failedToReturnCount(failedFailbackCount),
          ratioText: ratioSuffix,
          failedLink: { ...STATUS_TEXT.failback.failedLinkReturn, count: failedFailbackCount },
          issues: failbackIssues,
        }
      }
      // All resources succeeded despite process-level failure timestamp
      if (totalCount > 0 && successCount === totalCount) {
        return {
          state: AppStatusState.SUCCESS,
          text: STATUS_TEXT.failback.allReturned,
          ratioText: ratioSuffix,
          issues: failbackIssues,
        }
      }
      return {
        state: AppStatusState.ERROR,
        text: STATUS_TEXT.failback.failed,
        ratioText: ratioSuffix,
        issues: failbackIssues,
      }
  }

  return null
}
